import Page from '../components/page';
import withAuth from '../hocs/withAuth';
import React, { useContext, useEffect, useState } from 'react';
import { UserContext } from '../contexts/userContext';
import { Content, ContentWrapper, Step, StepWrapper } from '../components/vouchers/components';

import { GQLApp, GQLRecipientInput, GQLVoucher, GQLVoucherPurchase } from 'townapps-models/graphql/types';
import { v4 as uuidv4 } from 'uuid';
import gql from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/client';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { AlertContext } from '../contexts/alertContext';
import { Button, Modal, Typography } from '@material-ui/core';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { extractGraphQLError } from '../utils/graphqlErrors';
import media from 'styled-media-query';
import AppsModal from '../components/vouchers/appsModal';
import * as Sentry from '@sentry/nextjs';
import { NextSeo } from 'next-seo';
import useScreenView from '../hooks/useScreenView';
import InfoBar from '../components/infoBar';

const PhoneCard = dynamic(() => import('../components/vouchers/phoneCard'), { ssr: false });
const WhoCard = dynamic(() => import('../components/vouchers/whoCard'));
const AmountCard = dynamic(() => import('../components/vouchers/voucherAmountCard'), { ssr: false });
const MessageCard = dynamic(() => import('../components/vouchers/messageCard'), { ssr: false });
const PaymentsCard = dynamic(() => import('../components/vouchers/paymentCard'), { ssr: false });
const MultipleCard = dynamic(() => import('../components/vouchers/multipleCard'), { ssr: false });

const emailIsValid = (email: string) => {
  try {
    return validateEmail(email);
  } catch (e) {
    return false;
  }
};

import getAmplitude from '../utils/amplitude';
import validateEmail from '../utils/validateEmail';
import dynamic from 'next/dynamic';
import useLogView from '../hooks/useLogView';
import Link from 'next/link';

const ModalCard = styled(motion.div)`
  max-width: 90%;
  box-shadow: 0 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 5px 8px 0px rgba(0, 0, 0, 0.14), 0px 1px 14px 0px rgba(0, 0, 0, 0.12);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  outline: 0;
  background-color: #fafafa !important;
  border-radius: 8px;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  justify-content: center;
  padding: 32px;
  align-items: normal;

  ${media.lessThan('small')`
    max-width: 95%;
    width: 100%;
    padding: 16px;
    
    & h3 {
      font-size: 2rem !important;
      margin-bottom: 16px !important;
    }
  `}
`;

const prodKey = process.env.NEXT_PUBLIC_STRIPE_KEY || process.env.STRIPE_KEY;
const stripePromise = loadStripe(process.env.NODE_ENV === 'production' ? prodKey : 'pk_test_U3mjqyS8N13PHXNHnjNSpQQy');

const VOUCHER_PURCHASED = gql`
  mutation voucherPurchased($purchase: VoucherPurchase) {
    vouchers: voucherPurchased(purchase: $purchase) {
      id
    }
  }
`;

const VouchersPage = () => {
  const userCtx = useContext(UserContext);
  const alertCtx = useContext(AlertContext);
  const router = useRouter();

  const { data } = useQuery<{ apps: GQLApp[] }>(GET_APPS);
  const [voucherPurchased] = useMutation<{ vouchers: GQLVoucher[] }, { purchase: GQLVoucherPurchase }>(VOUCHER_PURCHASED, {
    fetchPolicy: 'no-cache',
  });

  const [loading, setLoading] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [vouchersModalOpen, setVouchersModalOpen] = useState(false);

  const [app, setApp] = useState(userCtx.user.app.vouchersEnabled ? userCtx.user.app : undefined);
  const [mode, setMode] = useState<'me' | 'friend' | 'multiple' | ''>('');
  const isForMe = mode === 'me';
  const isForFriend = mode === 'friend';
  const isMultiple = mode === 'multiple';

  const [amount, setAmount] = useState(undefined);

  const [friendEmail, setFriendEmail] = useState('');
  const [friendMessage, setFriendMessage] = useState('');
  const [friendName, setFriendName] = useState('');
  const [friendPhone, setFriendPhone] = useState('');

  const [voucherPhone, setVoucherPhone] = useState('');

  const [valid, setValid] = useState(false);

  const [recipients, setRecipients] = useState<GQLRecipientInput[]>([
    {
      id: uuidv4(),
      amount: 2000,
      valid: false,
    },
  ]);

  const apps = data?.apps ?? [];

  const enoughMoney = () => amount >= 2000 && amount <= 50000;
  const recipientsReady = () => {
    return recipients.length > 0 && recipients.every(r => r.amount >= 2000 && r.amount <= 50000 && emailIsValid(r.email) && r.name);
  };

  const isPaymentsReady = () => {
    if (isMultiple) {
      return amount >= 2000 && recipientsReady();
    } else if (isForFriend) {
      return enoughMoney() && friendEmail && friendMessage && friendName;
    } else {
      return enoughMoney();
    }
  };

  useEffect(() => {
    if (router.query?.app && apps && apps.length > 0) {
      const preselectedApp = apps.find(a => a.slug === router.query.app);

      if (preselectedApp && preselectedApp.vouchersEnabled && preselectedApp.isActive) {
        setApp(preselectedApp);
      } else {
        setApp(undefined);
        setVouchersModalOpen(true);
      }
    } else if (apps && apps.length > 0 && !app) {
      setVouchersModalOpen(true);
    }
  }, [apps]);

  useEffect(() => {
    if (recipients) setValid(!recipients.find(r => !r.valid));
  }, [recipients]);

  const purchase = {
    recipients: isMultiple ? recipients : [],
    friendEmail: isForFriend ? friendEmail : undefined,
    friendMessage: isForFriend ? friendMessage : undefined,
    friendName: isForFriend ? friendName : undefined,
    voucher: {
      appId: app?.id,
      boughtByID: userCtx.user.id,
      stripePaymentID: '',
      voucherQuantity: amount,
    },
  };

  useScreenView(app ? `${app.name} Local Vouchers` : null);
  useLogView(undefined, app?.id, 'VouchersPage');

  const features = userCtx.features;
  if (features && !features.find(f => f.feature === 'buy_voucher' && f.platform === 'web').active) {
    return (
      <Page>
        <div className="xplore-section">
          <h1 className="xplore-title text-center !mb-4">We'll be back in just a tick!</h1>
          <h2 className="xplore-subtitle !text-center !max-w-full !mb-0">Please bear with us while we fine-tune the voucher system.</h2>{' '}
          <h2 className="xplore-subtitle !text-center !max-w-full">
            In the meanwhile, you can find your vouchers in <Link href={'/profile?vouchers=true'}>your profile.</Link>
          </h2>
        </div>
      </Page>
    );
  }

  return (
    <Page noMarginTop>
      <NextSeo title={`${app ? app.name : 'Xplore'} Local Vouchers`} />
      <InfoBar title={'Buy a voucher'} subtitle={''} image={'/privacy.png'} />

      <ContentWrapper>
        <Content>
          <StepWrapper>
            <Step>Step 1</Step>
          </StepWrapper>

          <WhoCard
            loading={loading}
            setMode={m => {
              setMode(m);
              setRecipients([
                {
                  id: uuidv4(),
                  amount: 2000,
                  valid: false,
                },
              ]);
              setFriendName('');
              setFriendMessage('');
              setFriendEmail('');
            }}
            isForMe={isForMe}
            isForFriend={isForFriend}
            isMultiple={isMultiple}
          />

          {mode !== '' && mode !== 'multiple' && (
            <>
              <StepWrapper>
                <Step>Step 2</Step>
              </StepWrapper>

              <AmountCard loading={loading} apps={apps} app={app} setApp={setApp} amount={amount} setAmount={setAmount} />
            </>
          )}

          {mode === 'friend' && amount > 0 && (
            <>
              <StepWrapper>
                <Step>Step 3</Step>
              </StepWrapper>

              <MessageCard
                loading={loading}
                name={friendName}
                setName={setFriendName}
                setMessage={setFriendMessage}
                setEmail={setFriendEmail}
                message={friendMessage}
                email={friendEmail}
                setPhone={setFriendPhone}
                phone={friendPhone}
                setValid={setValid}
              />
            </>
          )}

          {mode === 'multiple' && (
            <>
              <StepWrapper>
                <Step>Step 2</Step>
              </StepWrapper>

              <MultipleCard
                loading={loading}
                apps={apps}
                app={app}
                setApp={setApp}
                amount={amount}
                setAmount={setAmount}
                recipients={recipients}
                setRecipients={setRecipients}
              />
            </>
          )}

          {mode !== '' && mode !== 'friend' && mode !== 'multiple' && amount > 0 && (
            <>
              <StepWrapper>
                <Step>Step 3</Step>
              </StepWrapper>

              <PhoneCard loading={loading} setPhone={setVoucherPhone} phone={voucherPhone} setValid={setValid} />
            </>
          )}

          <Elements stripe={stripePromise}>
            {isPaymentsReady() && valid && (
              <>
                <StepWrapper>
                  <Step>Step 4</Step>
                </StepWrapper>

                <PaymentsCard
                  app={app}
                  purchase={purchase}
                  amount={amount}
                  onPaymentTry={async (t, intent, cb) => {
                    if (t === 'succeed') {
                      try {
                        const res = await voucherPurchased({
                          variables: {
                            purchase: {
                              recipients: isMultiple ? recipients : [],
                              friendEmail: isForFriend ? friendEmail : undefined,
                              friendMessage: isForFriend ? friendMessage : undefined,
                              friendName: isForFriend ? friendName : undefined,
                              friendPhone: isForFriend ? friendPhone : undefined,
                              voucher: {
                                appId: app.id,
                                boughtByID: userCtx.user.id,
                                stripePaymentID: intent.id,
                                voucherQuantity: amount,
                                phone: voucherPhone,
                              },
                            },
                          },
                        });
                        cb();
                        if (res?.data?.vouchers && res?.data?.vouchers.length > 0) {
                          setSuccessModalOpen(true);
                          setLoading(false);
                        } else {
                          alertCtx.setError('Unexpected error, please go to your profile to see if the voucher purchase went through', false);
                          alertCtx.show(5000);
                          setLoading(false);
                        }
                      } catch (e) {
                        alertCtx.setError(extractGraphQLError(e), false);
                        alertCtx.show(5000);
                        Sentry.captureException(e);
                      }
                    } else {
                      setLoading(t === 'loading');
                    }
                  }}
                />
              </>
            )}
          </Elements>
        </Content>
      </ContentWrapper>

      <Modal
        open={successModalOpen}
        disableBackdropClick
        onClose={() => {
          setSuccessModalOpen(false);
        }}>
        <ModalCard
          animate={{
            scale: [1, 1.05, 1],
            x: ['-50%', '-50%', '-50%'],
            y: ['-50%', '-50%', '-50%'],
          }}
          transition={{ duration: 0.5 }}>
          <Typography variant="h3" style={{ marginBottom: 32, textAlign: 'center' }}>
            All Done!
          </Typography>
          <Typography variant="body1" style={{ textAlign: 'center' }}>
            Thank you for supporting {app?.name} and shopping locally.
          </Typography>
          <Typography variant="body1" style={{ textAlign: 'center', marginBottom: '2em' }}>
            Check your email for details.
          </Typography>

          <Button
            variant={'contained'}
            fullWidth
            color={'primary'}
            onClick={async () => {
              router.push('/profile?vouchers=true');
              const amplitude = await getAmplitude();
              amplitude.logEvent('Clicked on ok after voucher');
            }}>
            Ok
          </Button>
        </ModalCard>
      </Modal>

      <AppsModal
        disableBackdrop
        isAppSelectOpen={vouchersModalOpen}
        setIsAppSelectOpen={setVouchersModalOpen}
        setSelectedApp={a => {
          setApp(a);
          setVouchersModalOpen(false);
        }}
        selectedApp={app}
        apps={apps.filter(a => a.vouchersEnabled && a.isActive)}
      />
    </Page>
  );
};

export const GET_APPS = gql`
  query getData {
    apps: getAvailableApps {
      id
      name
      slug
      appDetail
      colour
      vouchersEnabled
      isActive
    }
  }
`;

export default withAuth(VouchersPage);
